import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { CheckCircle, XCircle, AlertTriangle, Info, X } from 'lucide-react';
import { Button } from './Button';

type ToastType = 'success' | 'error' | 'warning' | 'info';

type Toast = {
  id: string;
  message: string;
  type: ToastType;
  closing?: boolean;
};

// Matches .animate-toast-out's duration in index.css — the exit animation must finish playing
// before the toast is actually removed from state, otherwise it just vanishes instantly.
const EXIT_ANIMATION_MS = 180;

type ToastContextValue = {
  toast: (message: string, type?: ToastType) => void;
};

const ToastContext = createContext<ToastContextValue | null>(null);

const icons: Record<ToastType, ReactNode> = {
  success: <CheckCircle size={18} className="text-success" />,
  error: <XCircle size={18} className="text-danger" />,
  warning: <AlertTriangle size={18} className="text-warning" />,
  info: <Info size={18} className="text-info" />,
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  // Marks a toast as closing (plays the exit animation) then removes it from state once that
  // animation has actually finished — dismissing straight to a filter() cut the animation off
  // and made the toast just vanish instantly.
  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.map((t) => (t.id === id ? { ...t, closing: true } : t)));
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, EXIT_ANIMATION_MS);
  }, []);

  const toast = useCallback((message: string, type: ToastType = 'info') => {
    const id = Math.random().toString(36).slice(2);
    setToasts((prev) => [...prev, { id, message, type }]);
    setTimeout(() => dismiss(id), 4000);
  }, [dismiss]);

  return (
    <ToastContext.Provider value={{ toast }}>
      {children}
      {createPortal(
        <div role="status" aria-live="polite" className="fixed right-4 top-4 z-[9999] flex flex-col gap-2">
          {toasts.map((t) => (
            <div
              key={t.id}
              className={`flex items-center gap-3 rounded-xl border border-line bg-surface p-3 pr-4 shadow-lg min-w-[280px] max-w-sm ${
                t.closing ? 'animate-toast-out' : 'animate-toast-in'
              }`}
            >
              {icons[t.type]}
              <p className="flex-1 text-sm text-text">{t.message}</p>
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={() => dismiss(t.id)}
                className="!h-auto !w-auto !p-0 !text-muted hover:!text-text hover:!bg-transparent"
                aria-label="Dismiss"
              >
                <X size={14} />
              </Button>
            </div>
          ))}
        </div>,
        document.body,
      )}
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
}
